import { Injectable } from '@angular/core';
import { QuoteOrderSampleModel, RequestParamModel } from '../models';
import { PaginationModel } from '../models';
import { BaseService } from '../services';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { QuoteOrderSampleStatusModel } from "../models/quote-order-sample-status-model";
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class QuoteOrderSampleService {

  constructor(private baseService: BaseService) {
  }

  saveAsDraftQuoteOrder(quoteModel: any): Observable<number> {
    if (quoteModel.id !== undefined) {
      return this.baseService.post('quote-order/save-as-draft/' + quoteModel.id, quoteModel)
        .pipe(map<any, number>(response => {
          quoteModel = response.data as QuoteOrderSampleModel;
          return quoteModel;
        }),
          catchError(err => throwError(() => err))
        );
    } else {
      return this.baseService.post('quote-order/save-as-draft', quoteModel)
        .pipe(map<any, number>(response => {
          quoteModel = response.data as QuoteOrderSampleModel;
          return quoteModel;
        }),
          catchError(err => throwError(() => err))
        );
    }
  }

  saveWareHouseDate(body: any) {
    return this.baseService.post('quote-order/update-warehouse-arrival-date', body).pipe(map(response => {
      return response;
    }, ((error: any) => {
      return error;
    })))
  }

  orderCancel(body: any): Observable<any> {
    return this.baseService.post('quote-order/archive', body).pipe(map(response => {
      return response
    }),
      catchError(err => throwError(() => err))
    );
  }

  saveQuoteProductAppliedCalculator(quoteProductAppliedCalculatorModel: any): Observable<any> {
    return this.baseService.post('quote-product-applied-calculator/create', quoteProductAppliedCalculatorModel)
      .pipe(map<any, any>(response => {
        // ✅ Phase 3: Return full response with affected_calculators for frontend optimization
        // Backward compatible: returns id if affected_calculators not present
        if (response.data?.affected_calculators) {
          return response.data;
        }
        // Fallback for old response format
        quoteProductAppliedCalculatorModel = response.data as any;
        return quoteProductAppliedCalculatorModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  deleteQuoteProductAppliedCalculator(_id: any): Observable<any> {
    return this.baseService.delete('quote-product-applied-calculator/' + _id)
      .pipe(map<any, any>(response => {
        // ✅ Phase 3: Return full response with affected_calculators
        return response.data || response;
      }),
        catchError(err => throwError(() => err))
      );
  }

  changeStatus(orderModel: any): Observable<number> {
    if (orderModel.id !== undefined) {
      return this.baseService.patch('quote-order/status-update/' + orderModel.id, orderModel)
        .pipe(map<any, number>(response => {
          orderModel = response.data as QuoteOrderSampleModel;
          return orderModel.id;
        }),
          catchError(err => throwError(() => err))
        );
    } else {
      return this.baseService.post('quote-order/status-update', orderModel)
        .pipe(map<any, number>(response => {
          orderModel = response.data as QuoteOrderSampleModel;
          return orderModel.id;
        }),
          catchError(err => throwError(() => err))
        );
    }
  }

  changeOrderStatus(orderModel: any): Observable<number> {
    if (orderModel.id !== undefined) {
      return this.baseService.patch('quote/update-order-status/' + orderModel.id, orderModel)
        .pipe(map<any, number>(response => {
            orderModel = response.data as QuoteOrderSampleModel;
            return orderModel.id;
          }),
          catchError(err => throwError(() => err))
        );
    } else {
      return this.baseService.post('quote/update-order-status', orderModel)
        .pipe(map<any, number>(response => {
            orderModel = response.data as QuoteOrderSampleModel;
            return orderModel.id;
          }),
          catchError(err => throwError(() => err))
        );
    }
  }

  addDeliveryCharge(orderModel: any): Observable<number> {
    return this.baseService.patch('quote-order/add-delivery-charge/' + orderModel.id, orderModel)
      .pipe(map<any, number>(response => {
        orderModel = response.data as QuoteOrderSampleModel;
        return orderModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  deleteTrackingNumber(id: any) {
    return this.baseService.delete('sample-tracking-number/' + id)
      .pipe(map<any, number>(response => {
        return response;
      }),
        catchError(err => throwError(() => err))
      );
  }

  deleteAllTackingNumbers(id: any) {
    return this.baseService.delete('sample-tracking-number/delete-all/' + id)
      .pipe(map<any, number>(response => {
        return response;
      }),
        catchError(err => throwError(() => err))
      );
  }


  addTrackingNumber(orderModel: any): Observable<number> {
    return this.baseService.post('sample-tracking-number', orderModel)
      .pipe(map<any, number>(response => {
        orderModel = response.data as QuoteOrderSampleModel;
        return orderModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }


  // save user if new then add if old then update
  saveAccountAddress(accountModel: any): Observable<number> {
    return this.baseService.post('quote-orders', accountModel)
      .pipe(map<any, number>(response => {
        accountModel = response.data as QuoteOrderSampleModel;
        return accountModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  deleteAccountAddress(_id: any) {
    return this.baseService.delete('quote-orders/' + _id)
      .pipe(map<any, number>(response => {
        return response;
      }),
        catchError(err => throwError(() => err))
      );
  }

  // get single quote/order details.
  quotePaymentStatusListing(requestParamModel: RequestParamModel): Observable<any> {
    return this.baseService.get('quote-payment', requestParamModel)
      .pipe(map<any, any>(response => {
        return response.body as [];
      }),
        catchError(err => throwError(() => err))
      );
  }

  // save user if new then add if old then update
  saveShippingPrice(accountModel: any): Observable<number> {
    return this.baseService.patch('shipping-update/' + accountModel.id, accountModel)
      .pipe(map<any, number>(response => {
        accountModel = response.data as any;
        return accountModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  getTruckDetail(requestParamModel: any) {
    return this.baseService.get('shipping-truck-type', requestParamModel)
      .pipe(map<any, any>(response => {
        return response.body.data as any;
      }),
        catchError(err => throwError(() => err))
      );
  }

  getShippingCost(requestParamModel: any) {
    return this.baseService.post('shipping-calculation', requestParamModel)
      .pipe(map<any, any>(response => {
        return response.data as any;
      }),
        catchError(err => throwError(() => err))
      );
  }

  quoteExportPDF(idQuote: number, formData: any) {
    return this.baseService.uploadPost('quote/export/' + idQuote, formData).pipe(map<any, any>(response => {
      return response.data as any;
    }),
      catchError(err => throwError(() => err))
    );
  }

  exportConsumableSummaryPDF(id: number, formData: any) {
    return this.baseService.uploadPost('quote-order/export-consumable-summary/' + id, formData).pipe(map<any, any>(response => {
      return response.data as any;
    }),
      catchError(err => throwError(() => err))
    );
  }

  bigPostJobCreate(requestData: any) {
    return this.baseService.post('quote-order/push-at-big-post', requestData)
      .pipe(map<any, any>(response => {
        return response.data as any;
      }),
        catchError(err => throwError(() => err))
      );
  }

  sendMailForNominateDate(body: any) {
    return this.baseService.post('quote-order/send-mail', body).pipe(
      map(response => {
        return response
      }, (error: any) => {
        return error;
      })
    )
  }

  sendMail(body: any) {
    return this.baseService.post('quote-order/send-mail', body).pipe(
      map(response => {
        return response;
      }, (err: any) => {
        return err;
      })
    )
  }

  updateDeliveryAddress(orderModel: any): Observable<number> {
    return this.baseService.patch('quote-order/update-delivery-address/' + orderModel.quote_id, orderModel)
      .pipe(map<any, number>(response => {
        orderModel = response.data as QuoteOrderSampleModel;
        return orderModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  // download product sizing table
  generatesampleLabel(orderId: number): any {
    // return this.baseService.get('quote/generate-sample-label/' + orderId)
    return this.baseService.get('quote-order/generate-sample-label/' + orderId)
      .pipe(map<any, any>(response => {
        return response.body.data;
      }),
        catchError(err => throwError(() => err))
      );
  }

  generateCrateLabel(orderId: number): any {
    return this.baseService.get('quote-order/generate-crate-label/' + orderId)
      .pipe(map<any, any>(response => {
        return response.body.data;
      }),
        catchError(err => throwError(() => err))
      );;
  }

  /**
   * A PAGE of notes, with the pagination the list endpoint reports.
   *
   * `getNote` below hands back `body.data` and throws the headers away, which
   * is right for the callers that ask for a whole record's notes at once. A
   * server-filtered card needs the count of the FILTERED set to render "2 of 7
   * notes" and to know whether another page exists, and that only lives in
   * `X-Pagination-Total-Count`. Same mapping `ActivityService.getLists` uses,
   * so the two record tabs read their pagination identically.
   */
  getNotePage(requestModel: any, skipLoader: boolean = false) {
    return this.baseService.get('note', requestModel, skipLoader)
      .pipe(map<any, any>(response => {
        const list = new PaginationModel<any>();
        list.setHeaders(response.headers);
        list.data = response.body.data as any[];
        return list;
      }),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * The Internal Notes toolbar's dropdown contents for one record — the authors
   * who actually wrote on it and the people actually @-mentioned in it — plus
   * its unfiltered note count.
   *
   * A separate call from the list on purpose: the lists must describe the whole
   * record, so deriving them from a filtered response would let a filter hide
   * its own options.
   */
  getNoteFilterOptions(requestModel: any) {
    return this.baseService.get('note/filter-options', requestModel, true)
      .pipe(map<any, any>(response => response.body.data ?? {}),
        catchError(err => throwError(() => err))
      );
  }

  getNote(requestModel: any, skipLoader: boolean = false) {
    return this.baseService.get('note', requestModel, skipLoader)
      .pipe(map<any, any>(response => {
        return response.body.data as any;
      }),
        catchError(err => throwError(() => err))
      );
  }

  addUpdateNote(requestModel: any) {
    if (requestModel.noteId) {
      return this.baseService.patch('note/' + requestModel.noteId, requestModel)
        .pipe(map<any, number>(response => {
          requestModel = response.data as QuoteOrderSampleModel;
          return requestModel;
        }),
          catchError(err => throwError(() => err))
        );
    } else {
      return this.baseService.post('note', requestModel)
        .pipe(map<any, number>(response => {
          requestModel = response.data as QuoteOrderSampleModel;
          return requestModel;
        }),
          catchError(err => throwError(() => err))
        );

    }
  }

  addUpdateNoteWithAttachment(requestModel: any, noteId?: any) {
    if (noteId) {
      return this.baseService.uploadPost(`note/upload` ,requestModel).pipe(
        map<any, any>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
    } else {
      return this.baseService.uploadPost(`note`, requestModel).pipe(
        map<any, any>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
    }
  }

  downloadQuoteOrder(param: any) {
    // return this.baseService.getDownloadFileContent(`quote/download/${param.id}/${param.type}`);
    return this.baseService.post('quote-order/download', param)
      .pipe(map<any, any>(response => {
          return response.data as any;
        }),
        catchError(err => throwError(() => err))
      );
  }

  downloadQuoteOrderNew(body: any) {
    return this.baseService.getDownloadFileContent(`quote-order/download-file?file_name=${body.file_name}`);
  }

  shareQuoteOrder(param: any, quote_id: any) {
    return this.baseService.post('quote/export-receipt/' + quote_id, param)
      .pipe(map<any, any>(response => {
        return response.data as any;
      }),
        catchError(err => throwError(() => err))
      );;
  }

  getShippingRegions(requestParamModel: RequestParamModel): any {
    return this.baseService.get('shipping-region', requestParamModel)
      .pipe(map<any, any>(response => {
        const regionList = new PaginationModel<QuoteOrderSampleModel>();
        const headers = response.headers;
        regionList.setHeaders(headers);
        regionList.data = response.body.data as QuoteOrderSampleModel[];
        return regionList;
      }),
        catchError(err => throwError(() => err))
      );
  }

  getCarrierStatus(requestParamModel: RequestParamModel): any {
    return this.baseService.get('carrier-status', requestParamModel)
      .pipe(map<any, any>(response => {
        const carrierStatusList = new PaginationModel<QuoteOrderSampleModel>();
        const headers = response.headers;
        carrierStatusList.setHeaders(headers);
        carrierStatusList.data = response.body.data as QuoteOrderSampleModel[];
        return carrierStatusList;
      }),
        catchError(err => throwError(() => err))
      );;
  }

  updateQuoteOrder(orderModel: any): Observable<number> {
    return this.baseService.patch('quote/' + orderModel.id, orderModel)
      .pipe(map<any, number>(response => {
        orderModel = response.data as QuoteOrderSampleModel;
        return orderModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  updateQuoteOrderSaveAsDraft(orderModel: any): Observable<number> {
    return this.baseService.post('quote-order/save-as-draft/' + orderModel.id, orderModel)
      .pipe(map((response: any) => {
        const updatedOrder = response.data as QuoteOrderSampleModel;
        return updatedOrder.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  /**
   *
   * @param orderModel
   */
  updateDepositPayment(orderModel: any): Observable<number> {
    let id = orderModel.id;
    delete orderModel.id;
    if (id) {
      return this.baseService.patch('quote_summary/' + id, orderModel)
        .pipe(map<any, number>(response => {
            orderModel = response.data as QuoteOrderSampleModel;
            return orderModel.id;
          }),
          catchError(err => throwError(() => err))
        );
    } else {
      return this.baseService.post('quote_summary', orderModel)
        .pipe(map<any, number>(response => {
            orderModel = response.data as QuoteOrderSampleModel;
            return orderModel.id;
          }),
          catchError(err => throwError(() => err))
        );
    }
  }

  updateStatusOnAddressOrProductChange(orderModel: any): Observable<number> {
    let id = orderModel.id;
    delete orderModel.id;
    // Check if orderModel is empty and assign null if true
    orderModel = Object.keys(orderModel).length === 0 ? null : orderModel;

    return this.baseService.patch('quote/update-status-on-address-or-product-change/' + id, orderModel)
      .pipe(map<any, number>(response => {
        orderModel = response.data as QuoteOrderSampleModel;
        return orderModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  updateStockRequiredDate(orderModel: any): Observable<number> {
    let id = orderModel.id;
    delete orderModel.id;
    return this.baseService.patch('quote/update-stock-required-date/' + id, orderModel)
      .pipe(map<any, number>(response => {
        orderModel = response.data as QuoteOrderSampleModel;
        return orderModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  updateGPSMReference(orderModel: any): Observable<number> {
    return this.baseService.patch('quote-order/update-gpsm-reference/' + orderModel.id, orderModel)
      .pipe(map<any, number>(response => {
        orderModel = response.data as QuoteOrderSampleModel;
        return orderModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  updateQuoteOrderdetails(orderModel: any, data: any): Observable<number> {
    return this.baseService.patch('quote-order/' + orderModel, data)
      .pipe(map<any, number>((response) => {
        orderModel = response.data as QuoteOrderSampleModel;
        return orderModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  deleteNote(_id: any) {
    return this.baseService.delete('note/' + _id)
      .pipe(map<any, number>(response => {
        return response;
      }),
        catchError(err => throwError(() => err))
      );
  }

  pinNote(_id: any) {
    return this.baseService.patch('note/pin/' + _id, {})
      .pipe(map<any, any>(response => {
        return response.data;
      }),
        catchError(err => throwError(() => err))
      );
  }

  getCounter(requestParamModel: RequestParamModel) {
    return this.baseService.get('quote/counter', requestParamModel)
      .pipe(map<any, any>(response => {
        return response.body.data as any;
      }),
        catchError(err => throwError(() => err))
      );;
  }

  getTotalSampleProductCounter(requestParamModel: RequestParamModel) {
    return this.baseService.get('quote/total-sample-product-counter', requestParamModel)
      .pipe(map<any, any>(response => {
        return response.body.data as any;
      }),
        catchError(err => throwError(() => err))
      );;
  }

  confirmCarrierJob(requestBody: any): any {
    return this.baseService.post('quote-order/confirm-job', requestBody)
      .pipe(map<any, any>(response => {
        return response.data as any;
      }),
        catchError(err => throwError(() => err))
      );;
  }

  reCalculateQuotePrice(quoteModel: any): Observable<number> {
    return this.baseService.post('quote-summary', quoteModel)
      .pipe(map<any, number>(response => {
        quoteModel = response.data as any;
        return quoteModel.id;
      }),
        catchError(err => throwError(() => err))
      );
  }

  deleteAllQuoteProductAppliedCalculators(_id: any) {
    return this.baseService.delete('quote-order/delete-all-quote-product-applied-calculators/' + _id)
      .pipe(map<any, number>(response => {
        return response;
      }),
        catchError(err => throwError(() => err))
      );
  }

  generateExportId(id: any, requestParamModel: RequestParamModel) {
    return this.baseService.get('quote-order/generate-export-id/' + id, requestParamModel)
      .pipe(map<any, any>(response => {
        return response.body.data as any;
      }),
        catchError(err => throwError(() => err))
      );;
  }


  downloadFileContent(id: any) {
    return this.baseService.getDownloadFileContent(`note/download/${id}`);
  }


  deleteSelectedPdfFile(_id: any) {
    return this.baseService.delete('note/delete-attachment/' + _id)
      .pipe(map<any, number>(response => {
        return response;
      }),
        catchError(err => throwError(() => err))
      );
  }

  previewUploadFile(id: any) {
    return this.baseService.get('note/preview/' + id)
      .pipe(map<any, any>(response => {
        return response.body.data as any;
      }),
        catchError(err => throwError(() => err))
      );;
  }

  getWarehouseStatus(requestParamModel: RequestParamModel): any {
    return this.baseService.get('warehouse-status', requestParamModel)
      .pipe(map<any, any>(response => {
        const warehouseStatusList = new PaginationModel<QuoteOrderSampleModel>();
        const headers = response.headers;
        warehouseStatusList.setHeaders(headers);
        warehouseStatusList.data = response.body.data as QuoteOrderSampleModel[];
        return warehouseStatusList;
      }),
        catchError(err => throwError(() => err))
      );;
  }

  getOrderPackingSlip(requestModel: any) {
    return this.baseService.get('order-packing-slip', requestModel)
      .pipe(map<any, any>(response => {
        return response.body.data as any;
      }),
        catchError(err => throwError(() => err))
      );
  }

  addPackingSlip(requestModel: any) {
    return this.baseService.uploadPost(`order-packing-slip`, requestModel).pipe(
      map<any, any>(response => {
        return response.body;
      }),
      catchError(err => throwError(() => err))
    );
  }

  downloadPackingSlip(bodyData: any) {
    return this.baseService.getDownloadFileContent(`order-packing-slip/download?id=${bodyData.id}`);
  }

  previewPackingSlip(id: any) {
    return this.baseService.get('order-packing-slip/preview/' + id)
      .pipe(map<any, any>(response => {
        return response.body.data as any;
      }),
        catchError(err => throwError(() => err))
      );;
  }

  deleteSelectedPackingSlip(_id: any) {
    return this.baseService.delete('order-packing-slip/delete/' + _id)
      .pipe(map<any, number>(response => {
        return response;
      }),
        catchError(err => throwError(() => err))
      );
  }

  getPartialPaymentReceipt(requestModel: any) {
    return this.baseService.get('quote-order-payment-receipt', requestModel)
      .pipe(map<any, any>(response => {
        return response.body.data as any;
      }),
        catchError(err => throwError(() => err))
      );
  }

  addPartialPaymentReceipt(requestModel: any) {
    return this.baseService.uploadPost(`quote-order-payment-receipt`, requestModel).pipe(
      map<any, any>(response => {
        return response.body;
      }),
      catchError(err => throwError(() => err))
    );
  }

  downloadPartialPaymentReceipt(bodyData: any) {
    return this.baseService.getDownloadFileContent(`quote-order-payment-receipt/download?id=${bodyData.id}`);
  }

  downloadGPSMReferenceAttachment(bodyData: any) {
    return this.baseService.getDownloadFileContent(`quote-order/download-gpsm-reference-file?id=${bodyData.id}&gpsm_reference=${bodyData.gpsm_reference}`);
  }

  downloadPackegingSlip(bodyData: any) {
    return this.baseService.getDownloadFileContent(`order-packing-slip/download?id=${bodyData.id}`);
  }

  previewPartialPaymentReceipt(id: any) {
    return this.baseService.get('quote-order-payment-receipt/preview/' + id)
      .pipe(map<any, any>(response => {
        return response.body.data as any;
      }),
        catchError(err => throwError(() => err))
      );;
  }

  deleteSelectedPartialPaymentReceipt(_id: any) {
    return this.baseService.delete('quote-order-payment-receipt/delete/' + _id)
      .pipe(map<any, number>(response => {
        return response;
      }),
        catchError(err => throwError(() => err))
      );
  }

  getConsumableDetail(requestParamModel: RequestParamModel): any {
    return this.baseService.get('quote-product-applied-calculator', requestParamModel)
      .pipe(map<any, any>(response => {
        const regionList = new PaginationModel<QuoteOrderSampleModel>();
        const headers = response.headers;
        regionList.setHeaders(headers);
        regionList.data = response.body.data as any;
        return regionList;
      }),
        catchError(err => throwError(() => err))
      );;
  }

  // getQuoteOrderSampleStatus(requestParamModel: RequestParamModel): any {
  //   return this.baseService.get('quote-status', requestParamModel)
  //     .pipe(map<any, any>(response => {
  //       const quoteOrderSampleStatusList = new PaginationModel<QuoteOrderSampleStatusModel>();
  //       const headers = response.headers;
  //       quoteOrderSampleStatusList.setHeaders(headers);
  //       quoteOrderSampleStatusList.data = response.body.data as QuoteOrderSampleStatusModel[];
  //       return quoteOrderSampleStatusList;
  //     }),
  //       catchError(err => throwError(() => err))
  //     );;
  // }

  linkQuoteOrder(linkQuoteOrderModel: any, skipLoader: boolean = false): Observable<any> {
    return this.baseService.post('link-quote-order', linkQuoteOrderModel, skipLoader)
      .pipe(map<any, number>(response => {
        linkQuoteOrderModel = response as any;
        return linkQuoteOrderModel;
      }),
        catchError(err => throwError(() => err))
      );
  }

  updateDatesForProductionMilestone(requestModel: any): Observable<QuoteOrderSampleModel> {
    return this.baseService.post('quote-order/update-dates-for-production-milestone', requestModel)
      .pipe(
        map((response: any) => {
          const quoteOrderModel = response.data as QuoteOrderSampleModel;
          return quoteOrderModel;
        }),
        catchError((err: HttpErrorResponse) => {
          // Handle the error appropriately here
          throw err;
        })
      );
  }

  linkRemittanceWithPayment(linkRemittanceWithPaymentModel: any): Observable<any> {
    return this.baseService.post('quote-order/link-remittance-with-payment', linkRemittanceWithPaymentModel)
      .pipe(map<any, number>(response => {
        linkRemittanceWithPaymentModel = response.data as any;
        return linkRemittanceWithPaymentModel;
      }),
        catchError(err => throwError(() => err))
      );
  }

  sendNotification(requestModel: any) {
    return this.baseService.uploadPost(`notification`, requestModel).pipe(
      map<any, any>(response => {
        return response.body;
      }),
      catchError(err => throwError(() => err))
    );
  }

  uploadSamplePackingImage(body: any): Observable<any> {
    return this.baseService.post('sample-packing-image', body).pipe(map((response: any) => {
      return response.data as any;
    }, ((error: any) => {
      return error;
    })))
  }

  deleteSamplePackingImage(_id: any) {
    return this.baseService.delete('sample-packing-image/' + _id)
      .pipe(map<any, number>(response => {
        return response;
      }),
        catchError(err => throwError(() => err))
      );
  }

  updateSampleProductDescription(sampleId: any, data: any): Observable<any> {
    return this.baseService.patch('quote/update-sample-product-description/' + sampleId, data)
      .pipe(map<any, any>(response => response.data),
        catchError(err => throwError(() => err))
      );
  }

  updateExistingTender(data, _id: any) {
    return this.baseService.patch('quote/update-existing-tenders/' + _id, data).pipe(map((response: any) => {
      return response.data as any;
    }, ((error: any) => {
      return error;
    })))

  }

  assignOrderToSalesRep(data: any, orderId: any): Observable<any> {
    return this.baseService.patch('quote-order/' + orderId, data)
      .pipe(map<any, number>(response => {
        data = response.data as any;
        return data;
      }),
        catchError(err => throwError(() => err))
      );
  }

  createQuoteAddress(quoteAddress: any): Observable<any> {
    if (quoteAddress.id !== undefined) {
      let id = quoteAddress.id;
      delete quoteAddress.id;
      return this.baseService.patch('quote-address/' + id, quoteAddress)
        .pipe(map<any, any>(response => {
          return response.data;
        }), catchError(err => throwError(() => err)));
    } else {
      return this.baseService.post('quote-address', quoteAddress)
        .pipe(map<any, any>(response => {
          return response.data;
        }), catchError(err => throwError(() => err)));
    }
  }

  createQuoteSiteContact(quoteSiteContact: any): Observable<any> {
    if (quoteSiteContact.id !== undefined) {
      let id = quoteSiteContact.id;
      delete quoteSiteContact.id;
      return this.baseService.patch('quote-site-contact/' + id, quoteSiteContact)
        .pipe(map<any, any>(response => {
          return response.data;
        }), catchError(err => throwError(() => err)));
    } else {
      return this.baseService.post('quote-site-contact', quoteSiteContact)
        .pipe(map<any, any>(response => {
          return response.data;
        }), catchError(err => throwError(() => err)));
    }
  }

  createQuoteSummary(quoteSummary: any): Observable<any> {
    if (quoteSummary.id !== undefined) {
      let id = quoteSummary.id;
      delete quoteSummary.id;
      return this.baseService.patch('quote-summary/' + id, quoteSummary)
        .pipe(map<any, any>(response => {
          return response.data;
        }), catchError(err => throwError(() => err)));
    } else {
      return this.baseService.post('quote-summary', quoteSummary)
        .pipe(map<any, any>(response => {
          return response.data;
        }), catchError(err => throwError(() => err)));
    }
  }
}
